/**
 * Reveals content as it first scrolls into view (the "Reveal on scroll"
 * theme setting). Loaded from snippets/scripts.liquid only when that setting
 * is on.
 *
 * Markup opts in two ways:
 * - `data-reveal` on an element reveals that element.
 * - `data-reveal-children` on a container reveals each direct child (for
 *   lists whose items are rendered by blocks or snippets that shouldn't need
 *   to know about this).
 *
 * This script drives a `data-reveal` state on each target: `hidden` until it
 * enters the viewport, then `in` while its transition runs, then `done`. The
 * base fade-and-rise styles live in base.css; components can restyle the
 * same states (see blocks/_metro-collection-grid-item.liquid).
 *
 * Content stays visible unless this script hides it, so nothing is lost if
 * it fails or loads late. Anything already on screen (or above it) when the
 * page loads is left alone so first paint isn't delayed. Targets added later
 * (e.g. product recommendations arriving by fetch) are picked up through a
 * MutationObserver.
 *
 * Targets that enter together are staggered in DOM order, capped so long
 * rows don't drag. Siblings sitting at the same height but clipped off
 * screen horizontally (carousel slides) are shown immediately so they
 * don't animate mid-swipe later.
 *
 * Skipped entirely in the theme editor and for reduced motion.
 */

const TARGET_SELECTOR = '[data-reveal], [data-reveal-children] > *';
const STAGGER_MS = 80;
const MAX_STAGGER_STEPS = 5;
/** Longest reveal transition plus its own internal delays (see base.css and the collection grid item). */
const SETTLE_MS = 1400;

const designMode = /** @type {any} */ (window).Shopify?.designMode;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!designMode && !reducedMotion && 'IntersectionObserver' in window) {
  init();
}

function init() {
  const observer = new IntersectionObserver(handleIntersect, {
    rootMargin: '0px 0px -10% 0px',
  });

  /**
   * @param {ParentNode} root
   * @param {boolean} initial - true on page load: on-screen targets stay as they are.
   */
  const collect = (root, initial) => {
    /** @type {HTMLElement[]} */
    const targets = [];
    if (root instanceof HTMLElement && root.matches(TARGET_SELECTOR)) targets.push(root);
    for (const element of root.querySelectorAll(TARGET_SELECTOR)) {
      if (element instanceof HTMLElement) targets.push(element);
    }

    const fresh = targets.filter((element) => !isTracked(element));
    if (fresh.length === 0) return;

    // Read every position before writing any state to avoid layout thrash.
    // "On screen" matches the observer's root margin, so a row peeking just
    // above the fold at load still gets its reveal.
    const viewportHeight = window.innerHeight * 0.9;
    const positions = fresh.map((element) => element.getBoundingClientRect());

    fresh.forEach((element, index) => {
      const { top, bottom } = /** @type {DOMRect} */ (positions[index]);
      const alreadySeen = initial ? top < viewportHeight : bottom <= 0;
      if (alreadySeen) {
        element.dataset.reveal = 'done';
      } else {
        element.dataset.reveal = 'hidden';
        observer.observe(element);
      }
    });
  };

  /** @param {IntersectionObserverEntry[]} entries */
  function handleIntersect(entries) {
    const entering = entries
      .filter((entry) => entry.isIntersecting)
      .map((entry) => /** @type {HTMLElement} */ (entry.target))
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

    entering.forEach((element, index) => {
      observer.unobserve(element);
      reveal(element, Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS);
    });

    showClippedSiblings(entering);
  }

  /**
   * Carousel slides scrolled out of view horizontally never intersect, so
   * when a row enters, settle any pending sibling at the same height.
   * @param {HTMLElement[]} entering
   */
  function showClippedSiblings(entering) {
    const viewportHeight = window.innerHeight;
    const parents = new Set(entering.map((element) => element.parentElement));

    for (const parent of parents) {
      if (!parent) continue;
      for (const sibling of parent.children) {
        if (!(sibling instanceof HTMLElement) || sibling.dataset.reveal !== 'hidden') continue;
        const { top, bottom } = sibling.getBoundingClientRect();
        if (top < viewportHeight && bottom > 0) {
          observer.unobserve(sibling);
          sibling.dataset.reveal = 'done';
        }
      }
    }
  }

  collect(document, true);

  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof HTMLElement) collect(node, false);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

/** @param {HTMLElement} element */
function isTracked(element) {
  const state = element.dataset.reveal;
  return state === 'hidden' || state === 'in' || state === 'done';
}

/**
 * @param {HTMLElement} element
 * @param {number} delay - stagger delay in ms
 */
function reveal(element, delay) {
  element.style.setProperty('--reveal-delay', `${delay}ms`);
  element.dataset.reveal = 'in';

  // `done` drops the reveal transitions so the element's own (hover, etc.)
  // apply again.
  setTimeout(() => {
    element.dataset.reveal = 'done';
    element.style.removeProperty('--reveal-delay');
  }, delay + SETTLE_MS);
}
